import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface PixverseV55T2vParams {
  /**
   * 视频生成提示词
   */
  prompt: string;
  /**
   * 视频宽高比,默认16:9
   * @default "16:9"
   */
  aspect_ratio?: string;
  /**
   * 视频时长(秒),默认5秒
   * @default 5
   */
  duration?: number;
  /**
   * 视频质量,默认540p
   * @default "540p"
   * @values 540p | 720p | 1080p
   */
  quality?: '540p' | '720p' | '1080p';
  /**
   * 负向提示词,描述不希望出现的内容(可选)
   */
  negative_prompt?: string;
  /**
   * 随机种子,用于生成可复现的结果(可选)
   */
  seed?: number;
  /**
   * 视频风格
   * @values anime | 3d_animation | clay | comic | cyberpunk
   */
  style?: 'anime' | '3d_animation' | 'clay' | 'comic' | 'cyberpunk';
  /**
   * 模板ID(可选)
   */
  template_id?: number;
  /**
   * 音效内容描述
   */
  sound_effect_content?: string;
  /**
   * 口型同步TTS文本内容
   */
  lip_sync_tts_content?: string;
  /**
   * 口型同步TTS说话人ID
   */
  lip_sync_tts_speaker_id?: string;
  /**
   * 是否生成音频开关(V5.5特有)
   */
  generate_audio_switch?: boolean;
  /**
   * 是否生成多片段视频开关(V5.5特有)
   */
  generate_multi_clip_switch?: boolean;
  /**
   * 思考类型(V5.5特有)
   */
  thinking_type?: string;
}

export interface PixverseV55T2vResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * pixverse_v5_5_t2v
 *
 * <p>PixVerse V5.5 文生视频生成接口，支持通过文本提示词生成视频</p>
 * <p>API Documentation: https://docs.platform.pixverse.ai/text-to-video-generation-13016634e0</p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, pixverseV55T2v } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await pixverseV55T2v({
 *   // 参数见 PixverseV55T2vParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 PixverseV55T2vParams 接口定义
 * @returns 生成的资源列表
 */
export async function pixverseV55T2v(
  params: PixverseV55T2vParams,
): Promise<PixverseV55T2vResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'pixverse_v5_5_t2v',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: PixverseV55T2vResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
